import { useMemo } from 'react';
import { Flex } from '@mantine/core';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { AuditView } from '@dealstack/frontend/shared/components/Audit/Audit.tsx';
import {
	AuditContext,
	useAuditController,
} from '@dealstack/frontend/shared/components/Audit/useAuditController.ts';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { useMipManageBreadcrumbsPrefix } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { useMipSecurities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useMipSecurities.ts';

export const InvestmentHistoryPage = () => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const mipManageController = useMIPManageContext();
	const { data: investmentDetailsData } = useInvestmentDetailsSummary();
	const { data: mipSecurities } = useMipSecurities();

	const investorName = investmentDetailsData?.investor.displayName ?? '';

	const securitiesSelectOptions = useMemo(
		() =>
			(mipSecurities ?? []).map((security) => ({
				value: security.id,
				label: security.name,
				unitDenominator: security.unitDenominator,
			})),
		[mipSecurities]
	);

	const auditEndpoint = formatUnicorn(API_PATHS.AUDIT_INVESTMENT_INVESTMENTWORKFLOWID_GET, {
		investmentWorkflowId: investmentId,
	});
	const auditController = useAuditController(
		investmentId,
		auditEndpoint,
		securitiesSelectOptions
	);

	const programmeName = mipManageController?.mipData?.programmeName || 'Programme';
	const breadcrumbsPrefix = useMipManageBreadcrumbsPrefix({ dealId, mipId, programmeName });

	const breadcrumbs = useMemo<AddBreadcrumbItem[]>(
		() => [
			...breadcrumbsPrefix,
			{
				name: tMipManage('investment_details.breadcrumbs.investments'),
				url: mipManagePath(MIP_MANAGE_ROUTES.INVESTMENTS, { dealId, mipId }),
				hideInAppHeader: true,
			},
			{
				name: tMipManage('investment_details.breadcrumbs.investment_details'),
				url: mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_DETAILS, {
					dealId,
					mipId,
					investmentId,
				}),
				hideInAppHeader: true,
			},
			{
				name: tMipManage('investment_details.history.breadcrumb', {
					defaultValue: 'History',
				}),
				url: '',
				noLink: true,
				hideInAppHeader: true,
			},
		],
		[breadcrumbsPrefix, dealId, mipId, investmentId, tMipManage]
	);

	useBreadcrumbs(breadcrumbs);

	return (
		<PageAbsoluteContainer>
			<PageHeader
				hideBreadcrumbs
				pageTitle={
					investorName
						? tMipManage('investment_details.history.page_title', {
								defaultValue: "History - {{name}}'s Investment",
								name: investorName,
							})
						: tMipManage('investment_details.history.page_title_fallback', {
								defaultValue: 'History - Investment',
							})
				}
			/>

			<Flex direction="column" w="100%" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
				<AuditContext.Provider value={auditController}>
					<AuditView />
				</AuditContext.Provider>
			</Flex>
		</PageAbsoluteContainer>
	);
};
